export function normalizeMoney(value) {
    const n = Number(value ?? 0);
    return Number.isFinite(n) ? n : 0;
}

export function formatMoney(value) {
    const n = normalizeMoney(value);
    return `$${n.toFixed(2)}`;
}

export function formatMoneyFromCents(value) {
    const cents = Number(value ?? 0);
    const safe = Number.isFinite(cents) ? cents : 0;
    return `$${(safe / 100).toFixed(2)}`;
}

function sortByDurationThenPrice(a, b) {
    const dayDiff = Number(a?.duration_days || 0) - Number(b?.duration_days || 0);
    if (dayDiff !== 0) return dayDiff;
    return normalizeMoney(a?.price) - normalizeMoney(b?.price);
}

function normalizePassType(passType) {
    if (!passType) return null;

    return {
        ...passType,
        duration_days: Number(passType.duration_days || 0),
        price: normalizeMoney(passType.price),
        audience: String(passType.audience || "adult").toLowerCase(),
        category: String(passType.category || "").toLowerCase(),
        child_band: passType.child_band ? String(passType.child_band).toLowerCase().replace("grommett", "grommet") : null,
        age_min:
            passType.age_min == null || passType.age_min === ""
                ? null
                : Number(passType.age_min),
        age_max:
            passType.age_max == null || passType.age_max === ""
                ? null
                : Number(passType.age_max),
    };
}

export function buildCatalogResponse(payload) {
    const rawPassTypes = Array.isArray(payload?.pass_types)
        ? payload.pass_types
        : Array.isArray(payload)
            ? payload
            : [];

    const passTypes = rawPassTypes.map(normalizePassType).filter(Boolean);

    const adultPasses = passTypes
        .filter((passType) => passType.audience === "adult")
        .sort(sortByDurationThenPrice);

    const childPasses = passTypes
        .filter((passType) => passType.audience === "child")
        .sort(sortByDurationThenPrice);

    const singleDay =
        adultPasses.find(
            (passType) =>
                passType.duration_days === 1 ||
                passType.category === "single_day"
        ) || null;

    const multiDay = adultPasses.filter((passType) => passType.duration_days >= 2);

    const childByDurationAndBand = {};
    for (const childPass of childPasses) {
        const key = String(childPass.duration_days || 0);
        if (!childByDurationAndBand[key]) {
            childByDurationAndBand[key] = {
                baby_bum: null,
                little_bum: null,
                grommet: null,
                grommett: null,
            };
        }

        if (childPass.child_band === "baby_bum") {
            childByDurationAndBand[key].baby_bum = childPass;
        } else if (childPass.child_band === "little_bum") {
            childByDurationAndBand[key].little_bum = childPass;
        } else if (childPass.child_band === "grommet") {
            childByDurationAndBand[key].grommet = childPass;
            childByDurationAndBand[key].grommett = childPass;
        }
    }

    const childByDuration = {};
    for (const [duration, bands] of Object.entries(childByDurationAndBand)) {
        childByDuration[duration] = bands.grommet || bands.grommett || bands.little_bum || bands.baby_bum || null;
    }

    return {
        pass_types: passTypes,
        taxRate: Number(payload?.tax_rate ?? payload?.taxRate ?? 0),
        singleDay,
        multiDay,
        childByDuration,
        childByDurationAndBand,
    };
}

export function quoteSelection({
    adultPassType = null,
    babyBumPassType = null,
    babyBumQty = 0,
    grommetPassType = null,
    grommetQty = 0,
    grommettPassType = null,
    grommettQty = 0,
    littleBumPassType = null,
    littleBumQty = 0,
    childPassType = null,
    childQty = 0,
    taxRate = 0,
} = {}) {
    const adultPrice = normalizeMoney(adultPassType?.price);

    const safeBabyBumQty = Math.max(0, Number(babyBumQty || 0));
    const safeGrommettQty = Math.max(0, Number(grommetQty || grommettQty || 0));
    const safeLittleBumQty = Math.max(0, Number(littleBumQty || 0));

    let effectiveBabyBumPass = babyBumPassType;
    let effectiveBabyBumQty = safeBabyBumQty;
    let effectiveGrommettPass = grommetPassType || grommettPassType;
    let effectiveGrommettQty = safeGrommettQty;
    let effectiveLittleBumPass = littleBumPassType;
    let effectiveLittleBumQty = safeLittleBumQty;

    if (!effectiveBabyBumPass && !effectiveGrommettPass && !effectiveLittleBumPass && childPassType && childQty > 0) {
        effectiveGrommettPass = childPassType;
        effectiveGrommettQty = Math.max(0, Number(childQty || 0));
    }

    const babyBumUnitPrice = normalizeMoney(effectiveBabyBumPass?.price);
    const grommettUnitPrice = normalizeMoney(effectiveGrommettPass?.price);
    const littleBumUnitPrice = normalizeMoney(effectiveLittleBumPass?.price);

    const adultSubtotalCents = Math.round(adultPrice * 100);
    const babyBumSubtotalCents = Math.round(babyBumUnitPrice * 100) * effectiveBabyBumQty;
    const grommettSubtotalCents = Math.round(grommettUnitPrice * 100) * effectiveGrommettQty;
    const littleBumSubtotalCents = Math.round(littleBumUnitPrice * 100) * effectiveLittleBumQty;
    const subtotalCents =
        adultSubtotalCents +
        babyBumSubtotalCents +
        grommettSubtotalCents +
        littleBumSubtotalCents;
    const taxCents = Math.round(subtotalCents * Number(taxRate || 0));
    const totalCents = subtotalCents + taxCents;

    return {
        adultSubtotalCents,
        babyBumSubtotalCents,
        grommettSubtotalCents,
        littleBumSubtotalCents,
        subtotalCents,
        taxCents,
        totalCents,
    };
}